import { ArrowRight, MapPin } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { ASSETS, BRAND, MENU } from "@/lib/brand";
import {
  SectionEyebrow,
  SectionHeading,
  SectionWrap,
  FadeUp,
  CtaBand,
} from "@/components/sections/Primitives";

function CategoryBlock({ title, items, image, reverse = false, testId }) {
  return (
    <FadeUp className="grid md:grid-cols-12 gap-10 md:gap-16 items-start" >
      <div data-testid={testId} className={`md:col-span-5 ${reverse ? "md:order-2" : ""}`}>
        <div className="bg-pe-rose text-white inline-block px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-medium">
          {title}
        </div>
        {image && (
          <div className="mt-6 aspect-[5/6] overflow-hidden">
            <img src={image} alt={`${title} from Pizza Excellente`} className="w-full h-full object-cover" />
          </div>
        )}
      </div>
      <div className={`md:col-span-7 ${reverse ? "md:order-1" : ""}`}>
        <ul className="divide-y divide-pe-ink/10">
          {items.map((it, i) => (
            <li key={it.name} data-testid={`menu-item-${title.toLowerCase().replace(/\s+/g, "-")}-${i}`} className="py-6 flex items-start gap-6 group">
              <div className="font-serif text-3xl text-pe-rose/80 leading-none w-10 shrink-0">{String(i + 1).padStart(2, "0")}</div>
              <div className="flex-1">
                <h4 className="font-serif text-2xl md:text-3xl text-pe-ink leading-tight group-hover:text-pe-maroon transition-colors">{it.name}</h4>
                <p className="mt-2 text-pe-ink/65 leading-relaxed">{it.desc}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </FadeUp>
  );
}

export default function MenuLocationsPage() {
  useSeo({
    title: "Menu & Locations | Pizza Excellente",
    description:
      "View the Pizza Excellente menu concept and location strategy, with authentic thin-crust pizza, premium ingredients, and neighborhood expansion plans.",
    path: "/menu-locations",
    ogImage: ASSETS.margherita,
    ogImageAlt: "Pizza Excellente menu & locations",
  });

  return (
    <div data-testid="menu-locations-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-48 pb-16 md:pb-24 bg-pe-cream overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <SectionEyebrow>Menu & Locations</SectionEyebrow>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-pe-ink mt-6 leading-[1.02]">
              Fresh menu. Local focus. <span className="italic text-pe-maroon">Growing footprint.</span>
            </h1>
            <p className="mt-8 text-pe-ink/70 text-lg leading-relaxed max-w-xl">
              Pizza Excellente brings together authentic thin-crust pizza, selected complementary menu items, and a
              location strategy built around residential communities.
            </p>
            <a
              data-testid="menu-hero-cta"
              href={BRAND.mailto}
              className="inline-flex items-center gap-2 mt-10 bg-pe-maroon text-white hover:bg-pe-maroon-deep transition-colors px-8 py-4 text-[12px] tracking-[0.15em] font-medium uppercase"
            >
              Contact for Launch Updates <ArrowRight size={16} />
            </a>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={ASSETS.margherita} alt="Thin-crust pizza from Pizza Excellente" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Section 1 — Menu philosophy */}
      <SectionWrap testId="menu-philosophy" tone="white">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          <FadeUp className="md:col-span-5">
            <SectionHeading className="mt-5">Menu philosophy.</SectionHeading>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7 space-y-6 text-pe-ink/75 text-lg leading-relaxed">
            <p>
              The menu is intentionally simple, standardised, and centred on pizza — designed to support freshness, consistency,
              kitchen efficiency, and strong local appeal.
            </p>
            <p>
              Around <strong className="text-pe-ink">30 items</strong> at launch — primarily pizza, along with selected pasta dishes,
              appetizers, sandwiches, and desserts. Prices will be finalised closer to launch based on local-market review and menu engineering.
            </p>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Menu categories */}
      <SectionWrap testId="menu-pizzas" tone="cream">
        <CategoryBlock
          testId="menu-block-pizza"
          title="Pizza"
          items={MENU.pizzas}
          image={ASSETS.hero_pizza}
        />
      </SectionWrap>

      {/* Build your own */}
      <SectionWrap testId="menu-byo" tone="rose">
        <FadeUp className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow text-white/80">Create your own</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mt-4 leading-[1.05]">
              Thin Neapolitan-style crust. Your toppings.
            </h2>
            <p className="mt-6 text-white/85 leading-relaxed max-w-xl">
              Start with our pizza sauce and mozzarella. Add your favourites — gluten-free crust available (small).
            </p>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-6 text-white">
            <div>
              <div className="font-serif text-2xl mb-3">Standard toppings</div>
              <ul className="space-y-1 text-sm text-white/85">
                {MENU.toppings_standard.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
            <div>
              <div className="font-serif text-2xl mb-3">Specialty toppings</div>
              <ul className="space-y-1 text-sm text-white/85">
                {MENU.toppings_specialty.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
          </div>
        </FadeUp>
      </SectionWrap>

      <SectionWrap testId="menu-starters" tone="white">
        <CategoryBlock
          testId="menu-block-starters"
          title="Starters"
          items={MENU.starters}
          image={ASSETS.garlic_bread}
          reverse
        />
      </SectionWrap>

      <SectionWrap testId="menu-entrees" tone="cream">
        <CategoryBlock
          testId="menu-block-entrees"
          title="Entrées"
          items={MENU.entrees}
          image={ASSETS.pasta}
        />
      </SectionWrap>

      <SectionWrap testId="menu-sandwiches" tone="white">
        <CategoryBlock
          testId="menu-block-sandwiches"
          title="Sandwiches"
          items={MENU.sandwiches}
          image={ASSETS.ingredients}
          reverse
        />
      </SectionWrap>

      <SectionWrap testId="menu-desserts" tone="mauve">
        <CategoryBlock
          testId="menu-block-desserts"
          title="Desserts"
          items={MENU.desserts}
          image={ASSETS.tiramisu}
        />
      </SectionWrap>

      {/* Launch location */}
      <SectionWrap testId="menu-location-launch" tone="ink">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <FadeUp className="md:col-span-6">
            <div className="eyebrow text-pe-orange">Launch market</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-pe-cream mt-5 leading-[1.05]">
              Beginning in <span className="italic text-pe-rose-soft">Greater Noida, UP.</span>
            </h2>
            <p className="mt-8 text-pe-cream/75 text-lg leading-relaxed">
              The first target site is in or adjacent to a residential cluster with at least <strong className="text-pe-orange">5,000 residents nearby</strong>
              {" "}and approximately <strong className="text-pe-orange">25,000 residents within a 1-kilometre radius</strong>.
            </p>
            <p className="mt-5 text-pe-cream/75 text-lg leading-relaxed">
              The long-term growth plan combines company-owned and franchised units across Tier 2 and Tier 3 cities.
            </p>
            <a
              data-testid="menu-location-cta"
              href={BRAND.mailto}
              className="inline-flex items-center gap-2 mt-8 bg-pe-orange text-pe-ink hover:bg-white transition-colors px-8 py-4 text-[12px] tracking-[0.15em] font-medium uppercase"
            >
              Email for Menu or Location Updates <ArrowRight size={16} />
            </a>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-6">
            <div className="bg-pe-cream/5 border border-white/10 p-8">
              <div className="flex items-center gap-3 text-pe-orange">
                <MapPin size={20} />
                <div className="eyebrow text-pe-orange">First store · Coming soon</div>
              </div>
              <h3 className="font-serif text-3xl md:text-4xl text-pe-cream mt-5">Greater Noida, Uttar Pradesh</h3>
              <ul className="mt-6 space-y-4 text-pe-cream/80 text-sm">
                <li className="flex justify-between border-b border-white/10 pb-3">
                  <span>Target neighborhood density</span>
                  <span className="text-pe-cream">~25,000 within 1 km</span>
                </li>
                <li className="flex justify-between border-b border-white/10 pb-3">
                  <span>Apartment community</span>
                  <span className="text-pe-cream">5,000+ residents</span>
                </li>
                <li className="flex justify-between border-b border-white/10 pb-3">
                  <span>Format</span>
                  <span className="text-pe-cream">~700 sq ft · 70/30 takeout/dine-in</span>
                </li>
                <li className="flex justify-between">
                  <span>Free delivery radius</span>
                  <span className="text-pe-cream">1 km</span>
                </li>
              </ul>
            </div>
          </FadeUp>
        </div>
      </SectionWrap>

      <CtaBand
        testId="menu-cta-band"
        eyebrow="Stay close"
        heading="Be the first to know when we open."
        body="From menu reveals to launch dates and new neighborhood locations — drop us a line and we'll keep you posted."
        ctaLabel="Contact for Launch Updates"
        ctaHref={BRAND.mailto}
        secondaryLabel="See Franchise Opportunity"
        secondaryHref="/franchise-opportunity"
      />
    </div>
  );
}
